import * as Sentry from '@sentry/nextjs'
import { UploaderState } from './UploadDropzone'

export function getFileExtension(file: File) {
  return file?.name?.split('.')?.pop()?.toUpperCase() || ''
}

/**
 *
 * There is a bug with dropzone on MacOS with PSD files.
 * If you drop PSD file on dropzone, its mimetype [image/vnd.adobe.photoshop] will be detected correctly and file will be accepted.
 * But if you click on dropzone and try to upload file via default file dialog file.type field will be empty, so PSD file will be rejected.
 *
 * That's why we omit default [accept] property and use this custom validator instead.
 */

export const fileFormatValidator = (file) => {
  const fileExtension = getFileExtension(file)
  const acceptedMimetypes = [
    'image/png',
    'application/pdf',
    'image/svg+xml',
    'image/jpeg',
    'application/postscript',
    'image/vnd.adobe.photoshop',
    'font/ttf',
    'font/otf',
  ]
  const invalidType = acceptedMimetypes.indexOf(file.type) === -1
  const isPSD = fileExtension === 'PSD'

  if (invalidType && !isPSD) {
    return {
      code: 'invlaid-type',
      message: `Invalid type: ${file?.type || fileExtension}`,
    }
  }
  return null
}

export function canFileBeConvertedToSvg(file: File): boolean {
  const fileTypesThatCanConvertToSvg = [
    'image/png',
    'application/pdf',
    'image/svg+xml',
    'image/jpeg',
  ]

  const fileType = file?.type

  const fileExistsAndCanConvert =
    fileTypesThatCanConvertToSvg.includes(fileType)

  if (fileExistsAndCanConvert) {
    return true
  } else {
    const fileExtensionsThatCanBeConverted = [
      'SVG',
      'EPS',
      'PDF',
      'JPG',
      'JPEG',
      'PNG',
    ]
    const fileExtension = getFileExtension(file)
    return fileExtensionsThatCanBeConverted.includes(fileExtension)
  }
}

export const getFileNameFromFileUrl = (fileUrl: string) => {
  const fileNameAsArrayWithHash = fileUrl?.split('/')?.slice(-1)[0]?.split('.')
  fileNameAsArrayWithHash?.splice(1, 1)
  return fileNameAsArrayWithHash?.join('.')
}

export const logUploadErrorWithSentry = (error, fileToUpload) => {
  Sentry.captureException('User tried to upload custom design and failed.', {
    contexts: {
      detail: {
        uploadedFile: fileToUpload,
        error: error?.message,
      },
    },
  })
}

export async function getSvgData(file: File) {
  const formData = new FormData()
  const fields = { key: file.name }
  Object.entries({ ...fields, file }).forEach(([key, value]) => {
    formData.append(key, value)
  })
  const svgImage = await fetch('/api/magento/convertsvg/', {
    method: 'POST',
    body: formData,
  })
    .then((response) => response.json())
    .then(
      (convertedSvgImage: {
        svgData: string
        fileUrl: string
        svgImageUrl: string
        svgPath: string
      }) => {
        if (!convertedSvgImage) {
          throw new Error('Svg conversion returned empty response')
        }

        return convertedSvgImage
      }
    )
    .catch((error) => {
      Sentry.withScope((scope) => {
        scope.setContext('File', { fileName: file.name, fileSize: file.size })
        scope.setContext('Error', error)
        Sentry.captureException(
          new Error('Online Editor converting to SVG failed')
        )
      })
      throw error
    })
  return svgImage
}

export const UPLOADER_FALLBACK_COPY = {
  [UploaderState?.HOVER]: {
    title: "You've dragged... now drop!",
    subtitle: 'Drag and drop or click to browse computer',
  },
  [UploaderState?.SUCCESS]: {
    title: 'Uploaded files',
    subtitle: 'Select your product settings and checkout.',
  },
  [UploaderState?.IDLE]: {
    title: 'Upload your artwork',
    subtitle: 'Drag and drop or click to browse computer',
  },
}
